import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface ContactFormProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPlan?: string;
}

export default function ContactForm({ isOpen, onClose, selectedPlan = '' }: ContactFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Update message when selectedPlan changes
  useEffect(() => {
    if (selectedPlan) {
      setFormData(prev => ({
        ...prev,
        message: `I'm interested in the ${selectedPlan} plan. `
      }));
    }
  }, [selectedPlan]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await supabase.functions.invoke('handle-contact-form', {
        body: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          formType: 'contact',
          selectedPlan: selectedPlan || undefined,
        },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
      setTimeout(() => {
        onClose();
        setSubmitStatus('idle');
      }, 2000);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#1a1a1a] rounded-[24px] md:rounded-[32px] p-6 md:p-8 max-w-md w-full relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 md:top-6 md:right-6 text-white/60 hover:text-white text-2xl cursor-pointer w-8 h-8 flex items-center justify-center"
        >
          <i className="ri-close-line"></i>
        </button>

        <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">Get in Touch</h3>
        <p className="text-white/60 mb-6 text-sm md:text-base">
          {selectedPlan ? `You selected: ${selectedPlan}` : "We'll get back to you within 24 hours"}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            name="name"
            placeholder="Your Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            className="w-full h-12 md:h-14 px-4 md:px-5 bg-[#0a0a0a] border border-white/10 rounded-xl md:rounded-2xl text-white text-sm md:text-base placeholder:text-white/40 focus:border-[#0066ff] focus:outline-none focus:shadow-[0_0_20px_rgba(0,102,255,0.2)] transition-all"
          />
          <input
            type="email"
            name="email"
            placeholder="Email Address"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            className="w-full h-12 md:h-14 px-4 md:px-5 bg-[#0a0a0a] border border-white/10 rounded-xl md:rounded-2xl text-white text-sm md:text-base placeholder:text-white/40 focus:border-[#0066ff] focus:outline-none focus:shadow-[0_0_20px_rgba(0,102,255,0.2)] transition-all"
          />
          <input
            type="tel"
            name="phone"
            placeholder="Phone Number (optional)"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="w-full h-12 md:h-14 px-4 md:px-5 bg-[#0a0a0a] border border-white/10 rounded-xl md:rounded-2xl text-white text-sm md:text-base placeholder:text-white/40 focus:border-[#0066ff] focus:outline-none focus:shadow-[0_0_20px_rgba(0,102,255,0.2)] transition-all"
          />
          <textarea
            name="message"
            placeholder="Tell us about your project..."
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            required
            maxLength={500}
            rows={4}
            className="w-full px-4 md:px-5 py-3 md:py-4 bg-[#0a0a0a] border border-white/10 rounded-xl md:rounded-2xl text-white text-sm md:text-base placeholder:text-white/40 focus:border-[#0066ff] focus:outline-none focus:shadow-[0_0_20px_rgba(0,102,255,0.2)] transition-all resize-none"
          />

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-12 md:h-14 bg-[#8b2bd9] text-white text-base md:text-lg font-semibold rounded-xl md:rounded-2xl hover:bg-[#7a26c1] hover:translate-y-[-2px] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </button>

          {submitStatus === 'success' && (
            <div className="text-center text-[#0066ff] text-sm">
              ✓ Message sent! Check your email for confirmation.
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="text-center text-red-400 text-sm">
              Something went wrong. Please try again.
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
